import { assertNever } from "@suse-o11y/assert-never";
import { clamp } from "@suse-o11y/clamp";
import { Dialog, DialogClose, DialogTitle } from "@suse-o11y/dialog";
import { IconClose } from "@suse-o11y/icons";
import type { NonEmptyArray } from "@suse-o11y/non-empty-array";
import { useNow } from "@suse-o11y/use-now";
import React, { type ReactElement, useMemo, useState } from "react";

import type {
	MonitorCheckStatusConfiguredSilencingRule,
	MonitorCheckStatusExternalSilencingRule,
	MonitorCheckStatusSilencingRule,
} from "../../../../../../common/openapi";

import { UnmuteDrawerContext } from "./context";
import { ExternalLiveWithSelector } from "./external-live-with-selector";
import { ExternalLiveWithoutSelector } from "./external-live-without-selector";
import { ExternalPastWithSelector } from "./external-past-with-selector";
import { ExternalPastWithoutSelector } from "./external-past-without-selector";
import { InternalLiveWithSelector } from "./internal-live-with-selector";
import { InternalLiveWithoutSelector } from "./internal-live-without-selector";
import { InternalPastWithSelector } from "./internal-past-with-selector";
import { InternalPastWithoutSelector } from "./internal-past-without-selector";

interface Props {
	silencingRules: NonEmptyArray<MonitorCheckStatusSilencingRule>;
	onDelete: VoidFunction;
	isLiveMode: boolean;
}

type DrawerPath =
	| {
			_type:
				| "external-past-with-selector"
				| "external-past-without-selector"
				| "external-live-with-selector"
				| "external-live-without-selector";
			rule: MonitorCheckStatusExternalSilencingRule;
	  }
	| {
			_type:
				| "internal-past-with-selector"
				| "internal-past-without-selector"
				| "internal-live-with-selector"
				| "internal-live-without-selector";
			rule: MonitorCheckStatusConfiguredSilencingRule;
	  };

export const UnmuteMonitorDrawer = (props: Props) => (
	<Dialog className="drawer/dialog drawer-narrow">
		<DrawerForm {...props} />
	</Dialog>
);

function isExternalRule(rule: MonitorCheckStatusSilencingRule): rule is MonitorCheckStatusExternalSilencingRule {
	return rule._type === "MonitorCheckStatusExternalSilencingRule";
}

function isInternalRule(rule: MonitorCheckStatusSilencingRule): rule is MonitorCheckStatusConfiguredSilencingRule {
	return rule._type === "MonitorCheckStatusConfiguredSilencingRule";
}

function DrawerForm({ silencingRules, isLiveMode, onDelete }: Props) {
	const [currentRuleIndex, setCurrentRuleIndex] = useState(0);
	const currentRule = silencingRules[currentRuleIndex];

	const isPastInternalRule = useNow({
		interval: 100,
		floor: "seconds",
		pause: isLiveMode === false,
		transform: (now) => isInternalRule(currentRule) && currentRule.endTimeEpochMs < now,
	});

	const drawerPath = getDrawerPath({
		hasSelector: silencingRules.length > 1,
		isPastSilencingRule: isLiveMode === false || isPastInternalRule,
		rule: currentRule,
	});
	const contextValue = useMemo(
		() => ({
			ruleIndex: currentRuleIndex,
			ruleCount: silencingRules.length,
			onSelect: (index: number) => {
				setCurrentRuleIndex(clamp(index, { min: 0, max: silencingRules.length - 1 }));
			},
			onDelete,
		}),
		[currentRuleIndex, onDelete, silencingRules.length],
	);

	let pathComponent: ReactElement;

	switch (drawerPath._type) {
		case "external-past-with-selector":
			pathComponent = <ExternalPastWithSelector rule={drawerPath.rule} />;
			break;
		case "external-past-without-selector":
			pathComponent = <ExternalPastWithoutSelector rule={drawerPath.rule} />;
			break;
		case "external-live-with-selector":
			pathComponent = <ExternalLiveWithSelector rule={drawerPath.rule} />;
			break;
		case "external-live-without-selector":
			pathComponent = <ExternalLiveWithoutSelector rule={drawerPath.rule} />;
			break;
		case "internal-past-with-selector":
			pathComponent = <InternalPastWithSelector rule={drawerPath.rule} />;
			break;
		case "internal-past-without-selector":
			pathComponent = <InternalPastWithoutSelector rule={drawerPath.rule} />;
			break;
		case "internal-live-with-selector":
			pathComponent = <InternalLiveWithSelector rule={drawerPath.rule} />;
			break;
		case "internal-live-without-selector":
			pathComponent = <InternalLiveWithoutSelector rule={drawerPath.rule} />;
			break;
		default:
			return assertNever(drawerPath);
	}

	return (
		<UnmuteDrawerContext.Provider value={contextValue}>
			<header className="drawer-header">
				<DialogTitle className="drawer-title">Silencing rules ({contextValue.ruleCount})</DialogTitle>

				<DialogClose aria-label="Close" className="button button-square ml-auto">
					<IconClose className="size-3" />
				</DialogClose>
			</header>
			{pathComponent}
		</UnmuteDrawerContext.Provider>
	);
}

function getDrawerPath({
	hasSelector,
	isPastSilencingRule,
	rule,
}: {
	hasSelector: boolean;
	isPastSilencingRule: boolean;
	rule: MonitorCheckStatusSilencingRule;
}): DrawerPath {
	if (isExternalRule(rule)) {
		if (isPastSilencingRule) {
			return hasSelector
				? { _type: "external-past-with-selector", rule }
				: { _type: "external-past-without-selector", rule };
		}
		return hasSelector
			? { _type: "external-live-with-selector", rule }
			: { _type: "external-live-without-selector", rule };
	}

	if (isInternalRule(rule)) {
		if (isPastSilencingRule) {
			return hasSelector
				? { _type: "internal-past-with-selector", rule }
				: { _type: "internal-past-without-selector", rule };
		}
		return hasSelector
			? { _type: "internal-live-with-selector", rule }
			: { _type: "internal-live-without-selector", rule };
	}

	return assertNever(rule);
}
